import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faTimes } from "@fortawesome/pro-regular-svg-icons";
import {
    faCheckCircle,
    faDoNotEnter,
    faExclamationTriangle,
    faInfoCircle,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classnames from "classnames";
import { useEffect, useRef, useState } from "react";
import { useToast } from "../ToastProvider";
import "./style.css";

const DEFAULT_DURATION = 6000;

export enum ToastVariant {
    Error = "error",
    Info = "info",
    Success = "success",
    Warning = "warning",
}

export interface ToastOptions {
    variant: ToastVariant;
    autoDismiss: boolean;
    duration?: number;
}

export interface Toast {
    id: string;
    content: any;
    options: ToastOptions;
}

interface Props {
    toast: Toast;
}

interface ToastState {
    createdAt: number;
    duration: number;
    remainingDuration: number;
    isRunning: boolean;
}

// Warning: useInterval by Dan Abramov: https://overreacted.io/making-setinterval-declarative-with-react-hooks/
function useInterval(callback: Function, delay: number | null) {
    const savedCallback = useRef<any>(undefined);

    // Remember the latest callback.
    useEffect(() => {
        savedCallback.current = callback;
    }, [callback]);

    // Set up the interval.
    useEffect(() => {
        function tick() {
            savedCallback.current();
        }
        if (delay !== null) {
            let id = setInterval(tick, delay);
            return () => clearInterval(id);
        } else {
            return () => {};
        }
    }, [delay]);
}

export const ToastComponent = ({ toast }: Props) => {
    const [toastState, setToastState] = useState<ToastState>({
        createdAt: Date.now(),
        duration: toast.options.duration || DEFAULT_DURATION,
        remainingDuration: toast.options.duration || DEFAULT_DURATION,
        isRunning: true,
    });
    const { removeToast } = useToast();

    useInterval(
        () => {
            if (toast.options.autoDismiss) {
                if (toastState.remainingDuration < 0) {
                    removeToast(toast.id);
                } else {
                    setToastState((state) => ({
                        ...state,
                        remainingDuration: state.remainingDuration - 500,
                    }));
                }
            }
        },
        toastState.isRunning ? 500 : null
    );

    function handlePause() {
        if (toast.options.autoDismiss) {
            setToastState({
                ...toastState,
                isRunning: false,
            });
        }
    }

    function handleResume() {
        if (toast.options.autoDismiss) {
            setToastState({
                ...toastState,
                createdAt: Date.now(),
                remainingDuration: toastState.duration / 2,
                isRunning: true,
            });
        }
    }

    function getContainerVariantClassName() {
        switch (toast.options.variant) {
            case ToastVariant.Error:
                return "c-Toast-Toast-container-error";
            case ToastVariant.Success:
                return "c-Toast-Toast-container-success";
            case ToastVariant.Warning:
                return "c-Toast-Toast-container-warning";
            case ToastVariant.Info:
            default:
                return "c-Toast-Toast-container-info";
        }
    }

    function getIconVariantClassName() {
        switch (toast.options.variant) {
            case ToastVariant.Error:
                return "c-Toast-Toast-Icon-error";
            case ToastVariant.Success:
                return "c-Toast-Toast-Icon-success";
            case ToastVariant.Warning:
                return "c-Toast-Toast-Icon-warning";
            case ToastVariant.Info:
            default:
                return "c-Toast-Toast-Icon-info";
        }
    }

    function getIcon() {
        switch (toast.options.variant) {
            case ToastVariant.Error:
                return faDoNotEnter;
            case ToastVariant.Success:
                return faCheckCircle;
            case ToastVariant.Warning:
                return faExclamationTriangle;
            case ToastVariant.Info:
            default:
                return faInfoCircle;
        }
    }

    return (
        <div
            className={classnames(
                "c-Toast-Toast-container",
                getContainerVariantClassName(),
                toastState.remainingDuration < 500
                    ? "c-Toast-Toast-container-exiting"
                    : "c-Toast-Toast-container-entering"
            )}
            onMouseEnter={handlePause}
            onMouseLeave={handleResume}
        >
            <FontAwesomeIcon
                icon={getIcon() as IconProp}
                className={classnames(
                    "c-Toast-Toast-Icon",
                    getIconVariantClassName()
                )}
            />
            <div className="c-Toast-Toast-content">{toast.content}</div>
            <FontAwesomeIcon
                className="c-Toast-Toast-closeIcon"
                icon={faTimes as IconProp}
                onClick={() => removeToast(toast.id)}
            />
        </div>
    );
};
